import { useEffect } from "react";
import { useAppDispatch } from "app/hooks";
import type { AppDispatch } from "app/store";
import FormType from "enums/FormType";
import notificationService from "services/notificationService";
import {
    loadForm,
    qualityFormActions,
} from "features/qualityForm/qualityFormSlice";

export const MIN_SPINNER_MS = 750;

export function reloadFormForLiveUpdate(dispatch: AppDispatch, formId: string) {
    dispatch(qualityFormActions.startLiveUpdateReload());

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const minSpinner = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, MIN_SPINNER_MS);
    });

    const loadFormPromise = dispatch(
        loadForm({
            formType: FormType.EVALUATION,
            formId,
        })
    );

    // abort() rejects this promise; swallow so cancel/unmount is not an unhandled rejection
    Promise.all([loadFormPromise, minSpinner])
        .catch(() => undefined)
        .finally(() => {
            if (!cancelled) {
                dispatch(qualityFormActions.endLiveUpdateReload());
            }
        });

    return () => {
        cancelled = true;
        if (timer) {
            clearTimeout(timer);
        }
        loadFormPromise.abort();
    };
}

// Must match hawk-consumer's NotificationTopics.EVALUATION_FORM_EVENT_TOPIC
export const getEvaluationFormEventsTopic = (formId: string) =>
    `v2.quality.forms.evaluations.${formId}.events`;

/**
 * Subscribes to hawk events for the given evaluation form and reloads the form
 * when an event arrives. The notification WebSocket only exists while there is
 * at least one active subscription, so the connection lives exactly as long as
 * the component using this hook is mounted.
 */
export function useEvaluationFormLiveUpdates(
    evaluationFormId: string | undefined,
    enabled: boolean
) {
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!enabled || !evaluationFormId || evaluationFormId === "new") {
            return;
        }

        let cancelReload: (() => void) | undefined;

        const unsubscribe = notificationService.subscribe(
            getEvaluationFormEventsTopic(evaluationFormId),
            () => {
                cancelReload?.();
                cancelReload = reloadFormForLiveUpdate(
                    dispatch,
                    evaluationFormId
                );
            }
        );

        return () => {
            cancelReload?.();
            dispatch(qualityFormActions.endLiveUpdateReload());
            unsubscribe();
        };
    }, [dispatch, evaluationFormId, enabled]);
}
